import { useState } from "react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { usePMInbox, type InboxVoucher } from "@/hooks/use-pm-inbox";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { SrIcon } from "@/components/ui/SrIcon";
import VoucherApprovalModal from "@/components/project/VoucherApprovalModal";
import { InboxAccordion } from "./InboxAccordion";
import { RowGodkjenning } from "./RowGodkjenning";
import { RowBilag } from "./RowBilag";
import { RowFakturering } from "./RowFakturering";

interface SummaryCellProps {
  label: string;
  count: number;
  emphasized?: boolean;
}

function SummaryCell({ label, count, emphasized }: SummaryCellProps) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </span>
      <span
        className={
          emphasized
            ? "font-display font-semibold text-2xl leading-none tabular-nums mt-1"
            : "font-display font-semibold text-base leading-none tabular-nums mt-1"
        }
      >
        {count}
      </span>
    </div>
  );
}

/**
 * PL-innboks: type-basert oversikt over handlinger som venter på brukeren.
 * Tre kategorier — godkjenninger, bilag, fakturering. Stilen følger samme
 * mønster som MyTasksList (border-[1.5px] border-foreground bg-card).
 */
export function PMInboxSection() {
  const { activeUser } = useImpersonation();
  const { data, isLoading } = usePMInbox(activeUser?.id ?? null);
  // VIB-1226: én modal-instans for hele seksjonen, åpnet for det bilaget
  // brukeren trykket "Godkjenn" på. cleanupRef VIB-1226 fjerner denne
  // grenen når "feature_pm_inbox_approval" har stått på "all" en periode.
  const pmInboxApprovalFlag = useFeatureFlag("feature_pm_inbox_approval");
  const [activeVoucher, setActiveVoucher] = useState<InboxVoucher | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const inbox = data ?? { approvals: [], vouchers: [], invoices: [] };
  const total = inbox.approvals.length + inbox.vouchers.length + inbox.invoices.length;

  return (
    <div className="space-y-4">
      {/* Sammendrags-kort — matcher MyTasksList-stilen */}
      <div className="border-[1.5px] border-foreground bg-card p-4">
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <SummaryCell label="Til behandling" count={total} emphasized />
          <div className="w-px h-9 bg-border self-end" />
          <SummaryCell label="Godkjenninger" count={inbox.approvals.length} />
          <SummaryCell label="Bilag" count={inbox.vouchers.length} />
          <SummaryCell label="Fakturering" count={inbox.invoices.length} />
        </div>
      </div>

      <InboxAccordion
        iconName="check"
        title="Godkjenninger"
        description="Oppgaver fra teamet som venter på godkjenning"
        count={inbox.approvals.length}
        emptyText="Ingen oppgaver venter godkjenning."
      >
        {inbox.approvals.map((a) => (
          <RowGodkjenning key={a.id} task={a} />
        ))}
      </InboxAccordion>

      <InboxAccordion
        iconName="receipt"
        title="Inngående bilag"
        description="Eksterne kostnader påført prosjektet, må attesteres"
        count={inbox.vouchers.length}
        emptyText="Ingen bilag venter attestering."
      >
        {inbox.vouchers.map((v) => (
          <RowBilag key={v.id} voucher={v} onOpenApproval={setActiveVoucher} />
        ))}
      </InboxAccordion>

      <InboxAccordion
        iconName="receipt"
        title="Fakturering"
        description="Prosjekter som skal ha oppstarts- eller sluttfaktura"
        count={inbox.invoices.length}
        emptyText="Ingen prosjekter mangler faktura."
      >
        {inbox.invoices.map((inv) => (
          <RowFakturering key={`${inv.projectId}-${inv.invoiceType}`} invoice={inv} />
        ))}
      </InboxAccordion>

      {pmInboxApprovalFlag && (
        <VoucherApprovalModal
          voucherId={activeVoucher?.id ?? null}
          projectId={activeVoucher?.projectId ?? null}
          mode="approve"
          open={!!activeVoucher}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setActiveVoucher(null);
          }}
        />
      )}
    </div>
  );
}
